// DEM-2129 — Loops C · helpers puros de apresentação da UI de Loops.

import type { LoopStatus, LoopRunStatus, Loop } from '@/lib/types/loops';
import type { StatusType } from '@/components/ui/Badge';
import { humanizeSchedule, type LoopSchedule } from '@/lib/loops-rrule';

/** Rótulo humano do status do loop. */
export function loopStatusLabel(status: LoopStatus): string {
  const map: Record<LoopStatus, string> = {
    active: 'Ativo',
    paused: 'Pausado',
    completed: 'Concluído',
    archived: 'Arquivado',
  };
  return map[status] ?? status;
}

/** Mapeia status do loop pro StatusBadge do design system. */
export function loopStatusBadge(status: LoopStatus): StatusType {
  const map: Record<LoopStatus, StatusType> = {
    active: 'success',
    paused: 'warning',
    completed: 'idle',
    archived: 'cancelled',
  };
  return map[status] ?? 'idle';
}

/** Mapeia status de uma run pro StatusBadge. */
export function runStatusBadge(status: LoopRunStatus): StatusType {
  const map: Record<LoopRunStatus, StatusType> = {
    queued: 'pending',
    running: 'running',
    ok: 'success',
    failed: 'error',
    skipped: 'cancelled',
  };
  return map[status] ?? 'idle';
}

export function runStatusLabel(status: LoopRunStatus): string {
  const map: Record<LoopRunStatus, string> = {
    queued: 'Na fila',
    running: 'Rodando',
    ok: 'OK',
    failed: 'Falhou',
    skipped: 'Pulada',
  };
  return map[status] ?? status;
}

/** Referência de canal resolvida client-side (GET /v1/loops/channels). */
export interface LoopChannelRef {
  id: string;
  label: string;
  detail?: string | null;
}

/**
 * Nome REAL do agente alvo. Prioridade: projeção agent_name da API (backend
 * novo) → catálogo de agents carregado client-side → slug. O fallback existe
 * porque o backend VIVO pode ainda não mandar a projeção (janela até o restart).
 */
export function loopAgentName(
  loop: Pick<Loop, 'agent_name' | 'target_slug'>,
  agentNameBySlug?: Map<string, string>,
): string {
  const name = loop.agent_name;
  if (typeof name === 'string' && name.trim() !== '') return name;
  return agentNameBySlug?.get(loop.target_slug) ?? loop.target_slug;
}

/**
 * Canal de saída legível. Prioridade: projeção output_channel da API → canal
 * resolvido client-side pelo id (endpoint /channels, backend vivo) → alvo
 * companion → "na conversa"; id presente mas irresolvível → "Canal conectado"
 * (NUNCA afirmar "sem canal" quando a row TEM canal); só sem id → sem canal.
 */
export function loopChannelLabel(
  loop: Pick<
    Loop,
    'output_channel' | 'output_channel_connection_id' | 'target_is_companion' | 'agent_name' | 'target_slug'
  >,
  channelById?: Map<string, LoopChannelRef>,
): { label: string; muted: boolean } {
  const ch =
    loop.output_channel ??
    (loop.output_channel_connection_id ? channelById?.get(loop.output_channel_connection_id) ?? null : null);
  if (ch && ch.label) {
    return { label: ch.detail ? `${ch.label} (${ch.detail})` : ch.label, muted: false };
  }
  if (loop.target_is_companion) {
    return { label: `Na conversa de ${loopAgentName(loop)}`, muted: false };
  }
  if (loop.output_channel_connection_id) {
    return { label: 'Canal conectado', muted: false };
  }
  return { label: 'Sem canal de saída', muted: true };
}

/**
 * Periodicidade humanizada da Lista. Prefere `schedule_display.human`
 * (persistido no create/edit) e, na ausência, re-deriva de `schedule_display.ui`;
 * cai na RRULE crua como último recurso — nunca quebra.
 */
export function loopScheduleHuman(loop: Pick<Loop, 'schedule_display' | 'schedule_rrule'>): string {
  const display = loop.schedule_display as { human?: unknown; ui?: unknown } | null | undefined;
  if (display && typeof display.human === 'string' && display.human.trim()) {
    return display.human;
  }
  if (display && display.ui && typeof display.ui === 'object') {
    try {
      return humanizeSchedule(display.ui as LoopSchedule);
    } catch {
      /* ignore — cai na rrule crua */
    }
  }
  return loop.schedule_rrule;
}

/** Formata um valor possivelmente ausente como '—' (degradação graciosa). */
export function orDash(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

/** Duração de uma run (finished - started) formatada, ou '—'. */
export function runDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m${rem}s` : `${m}m`;
}

/**
 * Extrai um link legível de dispatch a partir de dispatch_ref (degrada com '—').
 * Reconhece pipeline_run_id / execution_id / demand_id comuns do Core.
 */
export function dispatchLink(
  dispatchRef: Record<string, unknown> | null,
): { label: string; href: string | null } {
  if (!dispatchRef || typeof dispatchRef !== 'object') return { label: '—', href: null };
  const ref = dispatchRef as Record<string, unknown>;
  const pipelineRunId = ref['pipeline_run_id'];
  if (typeof pipelineRunId === 'string' && pipelineRunId) {
    return { label: 'pipeline run', href: `/pipeline-runs/${pipelineRunId}` };
  }
  const demandId = ref['demand_id'];
  if (typeof demandId === 'string' && demandId) {
    return { label: 'demanda', href: `/mc/demands` };
  }
  const executionId = ref['execution_id'];
  if (typeof executionId === 'string' && executionId) {
    return { label: executionId.slice(0, 8), href: null };
  }
  return { label: '—', href: null };
}
